import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Input, OnChanges, OnDestroy, ViewChild } from '@angular/core';

import * as echarts from 'echarts';

import { TimelineConfig } from './tachograph-chart.types';
import { clampSegments, toDayBounds } from './tachograph-chart.utils';
import { buildTimelineOption } from './tachograph-chart.factory';
import { ITachographDaySegment } from '@src/app/interfaces/tachograph.interface';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-tachograph-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './tachograph-chart.component.html',
  styleUrls: ['./tachograph-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TachographChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('host', { static: true }) host!: ElementRef<HTMLDivElement>;

  @Input({ required: true }) segments: ITachographDaySegment[] = [];
  @Input() day?: Date;
  @Input() config?: TimelineConfig;

  private chart?: echarts.ECharts;
  private ro?: ResizeObserver;

  constructor(
    private translate: TranslateService,
  ) {}

  ngAfterViewInit(): void {
    this.chart = echarts.init(this.host.nativeElement);
    this.render();

    if ('ResizeObserver' in window) {
      this.ro = new ResizeObserver(() => this.chart?.resize());
      this.ro.observe(this.host.nativeElement);
    } else {
      (window as any).addEventListener('resize', this.onResize);
    }
  }

  ngOnChanges(): void {
    if (this.chart) this.render();
  }

  ngOnDestroy(): void {
    this.ro?.disconnect();
    window.removeEventListener('resize', this.onResize);
    this.chart?.dispose();
  }

  private onResize = () => this.chart?.resize();

  private render(): void {
    if (!this.chart) return;
    if (!this.segments?.length) {
      this.chart.clear();
      return;
    }
    const day = this.day ?? new Date(this.segments[0].From);
    const bounds = toDayBounds(day);
    const clamped = clampSegments(this.segments, bounds);
    const option: echarts.EChartsOption = buildTimelineOption({ data: clamped, bounds, config: this.config, translateService: this.translate });
    this.chart.setOption(option, true);
  }
}
